// ─── Impostazioni → Rubrica Telegram ─────────────────────────────────────────
//
// ⚠️⚠️ **NON È UNA LISTA PIATTA DI RAGGIUNGIBILI: È UNA RUBRICA PER
// PROPRIETARIO.** La riga dice *«questa persona ha questa persona in rubrica»*,
// e lo stesso nome compare una volta per ogni persona che ce l'ha. È normale, e
// questa schermata lo mostra a colpo d'occhio raggruppando per proprietario.
//
// 📏 Il 14/09/2026, sul sistema di oggi, sette righe per cinque persone sono
// sembrate doppioni a una sonda che guardava due colonne sole, e stavo per
// cancellarne due — cioè togliere un contatto dalla rubrica di qualcuno.
// 📌 *La ripetizione di un nome non è una riga ripetuta finché non si è
// guardato cosa distingue le righe.*
//
// ⇒ E da lì la regola di consegna: **un avviso parte una volta sola per
//   persona**, perché il destinatario è una CHAT, non una riga.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { ContattoRubrica } from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { Attesa, Guasto, Vuoto } from '../../components/pezzi.js';
import { SoloLettura, usaModifica, usaRilettura } from './comune.js';

const CHIAVE = 'rubrica-telegram';

interface Persona {
  id: string;
  displayName: string;
}

export function Rubrica(): React.JSX.Element {
  const modificabile = usaModifica('manage_settings');
  const elenco = useQuery({
    queryKey: [CHIAVE],
    queryFn: () => chiama<{ items: ContattoRubrica[] }>('GET', '/settings/telegram-contacts'),
  });

  if (elenco.isLoading) return <Attesa cosa="la rubrica" />;
  if (elenco.error) return <Guasto errore={elenco.error} />;
  const items = elenco.data?.items ?? [];

  const perProprietario = new Map<string, ContattoRubrica[]>();
  for (const c of items) {
    const gia = perProprietario.get(c.ownerMemberId) ?? [];
    gia.push(c);
    perProprietario.set(c.ownerMemberId, gia);
  }
  // Le PERSONE raggiungibili, che è un numero diverso dal numero di righe — ed è
  // quello che conta quando si chiede «a quanti arriva un avviso».
  const raggiungibili = new Set(items.filter((c) => c.chatId).map((c) => c.memberId)).size;

  return (
    <section>
      <header className="testata">
        <h2>Rubrica Telegram</h2>
        <p className="tenue">
          {items.length} righe · {perProprietario.size} proprietari · <strong>{raggiungibili} persone</strong>{' '}
          raggiungibili. Lo stesso nome può comparire più volte: è normale, sta nella rubrica di più persone.
          L’avviso però gli arriva <strong>una volta sola</strong>.
        </p>
        <SoloLettura mostra={modificabile} />
      </header>

      {items.length === 0 ? (
        <Vuoto
          titolo="Rubrica vuota"
          perché="Senza un codice di chat il circolo non ha modo di raggiungere nessuno su Telegram."
        />
      ) : (
        [...perProprietario.entries()].map(([ownerId, righe]) => (
          <div key={ownerId} className="riquadro">
            <h3>{righe[0]?.ownerName}</h3>
            <div className="tabella-scorre">
              <table>
                <thead>
                  <tr>
                    <th>Ha in rubrica</th>
                    <th>Chat</th>
                    <th>Etichetta</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {righe.map((c) => (
                    <RigaContatto
                      key={`${c.ownerMemberId}|${c.memberId}`}
                      contatto={c}
                      modificabile={modificabile}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}

      {modificabile && <Nuovo />}

      {/* 🔔 «Avvisi ai soci» è tornata nel menu il 25/09/2026: questo rimando
          resta, perché lì si dice anche chi dello staff riceve le notifiche su
          Telegram, e chi è in rubrica lo cerca da qui. */}
      <p className="tenue piccolo">
        <Link to="/impostazioni/avvisi">Avvisi ai soci e notifiche allo staff →</Link>
      </p>
    </section>
  );
}

function RigaContatto({
  contatto,
  modificabile,
}: {
  contatto: ContattoRubrica;
  modificabile: boolean;
}): React.JSX.Element {
  const rileggi = usaRilettura(CHIAVE);
  const togli = useMutation({
    meta: { fatto: `${contatto.memberName} tolto dalla rubrica di ${contatto.ownerName}` },
    mutationFn: () =>
      chiama('DELETE', `/settings/telegram-contacts/${contatto.ownerMemberId}/${contatto.memberId}`),
    onSuccess: rileggi,
  });

  return (
    <tr>
      <td>{contatto.memberName}</td>
      <td className={contatto.chatId ? '' : 'tenue'}>{contatto.chatId ?? 'nessuna'}</td>
      <td className="tenue">{contatto.label ?? '—'}</td>
      <td className="azioni">
        {modificabile && (
          <button
            type="button"
            className="piatto"
            disabled={togli.isPending}
            onClick={() => {
              if (confirm(`Togliere ${contatto.memberName} dalla rubrica di ${contatto.ownerName}?`)) {
                togli.mutate();
              }
            }}
          >
            Togli
          </button>
        )}
        <Guasto errore={togli.error} />
      </td>
    </tr>
  );
}

/** Cerca una persona per nome. Un id non si digita a mano. */
function Cerca({
  etichetta,
  scelta,
  onScegli,
}: {
  etichetta: string;
  scelta: Persona | null;
  onScegli: (p: Persona | null) => void;
}): React.JSX.Element {
  const [q, setQ] = useState('');
  const trovati = useQuery({
    queryKey: ['cerca-persone', q],
    queryFn: () => chiama<{ items: Persona[] }>('GET', `/members/lookup?q=${encodeURIComponent(q)}&limit=8`),
    enabled: q.trim().length >= 2 && scelta === null,
  });

  if (scelta) {
    return (
      <p className="scelto">
        <strong>{etichetta}:</strong> {scelta.displayName}{' '}
        <button type="button" className="piatto" onClick={() => onScegli(null)}>
          cambia
        </button>
      </p>
    );
  }

  return (
    <div className="cerca-persona">
      <label>
        {etichetta}
        <input value={q} placeholder="cognome…" onChange={(e) => setQ(e.target.value)} />
      </label>
      {(trovati.data?.items ?? []).map((p) => (
        <button key={p.id} type="button" className="piatto" onClick={() => onScegli(p)}>
          {p.displayName}
        </button>
      ))}
    </div>
  );
}

function Nuovo(): React.JSX.Element {
  const rileggi = usaRilettura(CHIAVE);
  const [proprietario, setProprietario] = useState<Persona | null>(null);
  const [chi, setChi] = useState<Persona | null>(null);
  const [chat, setChat] = useState('');
  const [etichetta, setEtichetta] = useState('');

  const crea = useMutation({
    meta: { fatto: 'contatto aggiunto alla rubrica' },
    mutationFn: () =>
      chiama('POST', '/settings/telegram-contacts', {
        ownerMemberId: proprietario!.id,
        memberId: chi!.id,
        chatId: chat.trim() === '' ? null : chat.trim(),
        label: etichetta.trim() === '' ? null : etichetta.trim(),
      }),
    onSuccess: () => {
      setChi(null);
      setChat('');
      setEtichetta('');
      rileggi();
    },
  });

  return (
    <form
      className="riquadro"
      onSubmit={(e) => {
        e.preventDefault();
        if (proprietario && chi) crea.mutate();
      }}
    >
      <h3>Aggiungi in rubrica</h3>
      <p className="tenue piccolo">
        Il codice della chat è un numero, e lo dice il bot a chi gli scrive. Senza, la persona resta in
        rubrica ma il circolo non la può raggiungere.
      </p>
      <Cerca etichetta="Di chi è la rubrica" scelta={proprietario} onScegli={setProprietario} />
      <Cerca etichetta="Chi ci va dentro" scelta={chi} onScegli={setChi} />
      <label>
        Codice della chat
        <input
          value={chat}
          inputMode="numeric"
          placeholder="1256773674"
          onChange={(e) => setChat(e.target.value)}
        />
      </label>
      <label>
        Etichetta
        <input
          value={etichetta}
          placeholder="come la chiama chi ce l’ha"
          onChange={(e) => setEtichetta(e.target.value)}
        />
      </label>
      <Guasto errore={crea.error} />
      <button type="submit" disabled={crea.isPending || !proprietario || !chi}>
        {crea.isPending ? 'Un attimo…' : 'Aggiungi'}
      </button>
    </form>
  );
}
